import { Box, Text } from 'braid-design-system';
import classNames from 'classnames';
import React, { useRef, useState, useEffect, forwardRef } from 'react';

import * as styles from './styles.css';

interface VerificationCodeInputPropTypes {
  value?: string;
  length: number;
  validChars?: string;
  placeholder?: string;
  autoFocus?: boolean;
  onChange?: any;
  onFocus?: any;
  onBlur?: any;
  onComplete?: any;
}

const VerificationCodeInput = forwardRef(
  (
    {
      value,
      length,
      validChars,
      placeholder,
      autoFocus,
      onChange,
      onFocus,
      onBlur,
      onComplete,
    }: VerificationCodeInputPropTypes,
    ref,
  ) => {
    const [localValue, setLocalValue] = useState('');
    const [isActive, setActive] = useState(false);

    const inputRef = useRef<any>(null);

    useEffect(() => {
      if (autoFocus && inputRef.current !== null) {
        inputRef.current.focus();
      }
    }, [autoFocus]);

    const handleClick = () => {
      inputRef.current.focus();
    };

    const handleKeyDown = (event: {
      key: string;
      preventDefault: () => void;
    }) => {
      if (
        ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)
      ) {
        // do not allow to change cursor position
        event.preventDefault();
      }
    };

    const handleInputChange = (event: any) => {
      const newInputVal = event.target.value.replace(/\s/g, '');

      if (RegExp(`^[${validChars}]{0,${length}}$`).test(newInputVal)) {
        if (onChange) {
          onChange?.(newInputVal);
        }
        setLocalValue(newInputVal);

        if (newInputVal.length === length) {
          onComplete?.(newInputVal);
        }
      }
    };

    const getValue = () => value ?? localValue;

    const isCharacterSelected = (i: number) => {
      const code = getValue();
      return (
        (code.length === i || (code.length === i + 1 && length === i + 1)) &&
        isActive
      );
    };

    return (
      <>
        <div
          data-testid="container"
          className={styles.container}
          onClick={() => inputRef.current.focus()}
        >
          <input
            id="verificationCodeInput"
            aria-label="verification input"
            spellCheck={false}
            value={getValue()}
            onChange={handleInputChange}
            ref={(node) => {
              inputRef.current = node;
              if (typeof ref === 'function') {
                ref(node);
              } else if (ref) {
                ref.current = node;
              }
            }}
            className={styles.verificationInput}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              setActive(true);
              onFocus?.();
            }}
            onBlur={() => {
              setActive(false);
              onBlur?.();
            }}
            onSelect={(e: any) => {
              const val = e.target.value;
              e.target.setSelectionRange(val.length, val.length);
            }}
          />
          {[...Array(length)].map((_, i: number) => (
            <React.Fragment key={`field-${i}`}>
              <div
                className={classNames(
                  styles.character,
                  isCharacterSelected(i) && styles.characterSelected,
                )}
                onClick={handleClick}
                id={`field-${i}`}
                data-testid={`character-${i}`}
                key={i}
              >
                {getValue()[i] || placeholder}
              </div>
              {length === 6 && i === 2 && (
                <Box display="flex" alignItems="center">
                  <Text>-</Text>
                </Box>
              )}
            </React.Fragment>
          ))}
        </div>
      </>
    );
  },
);

VerificationCodeInput.displayName = 'VerificationCodeInput';

export default VerificationCodeInput;
